import i18n, { type InitOptions } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpBackend from "i18next-http-backend";
import {
    TranslationNamespace,
    type TranslationManifestResponse,
} from "../data/models/schemas";
import {
    I18N_DEBUG_MODE,
    I18N_STORE_KEYS_TO_VARIABLE,
} from "../components/utilities/FeatureFlags";
import { getApiPath } from "../components/utilities/ApiUtils";
import { setOnWindow } from "../components/utilities/WindowUtils";

const MERGED_NAMESPACE = TranslationNamespace.TRANSLATION;

export type ApplicationLocaleOption = {
    value: string;
    label: string;
};

export type TranslationRuntimeConfig = {
    manifest: TranslationManifestResponse;
    applicationLocales: ApplicationLocaleOption[];
    defaultLocale: string;
};

const setObjectMap = (
    map: string,
    language: string,
    key: string,
    value: string
) => {
    const theMap = window[map] || {};

    if (!theMap[language]) {
        theMap[language] = {};
    }

    if (!theMap[language][key]) {
        theMap[language][key] = value;
        window[map] = theMap;
    }
};

const setWindowObject = (
    language: string,
    key: string,
    value: string,
    missing: boolean = false
) => {
    if (I18N_STORE_KEYS_TO_VARIABLE()) {
        setObjectMap("i18allKeys", language, key, value);

        if (missing) {
            setObjectMap("i18missingKeys", language, key, value);
        }
    }
};

if (I18N_STORE_KEYS_TO_VARIABLE()) {
    setOnWindow("i18allKeys", {});
    setOnWindow("i18byContent", {});
}

const missingKeyHandler = (
    lngs: readonly string[],
    _ns: string,
    key: string,
    fallbackValue: string
) => {
    lngs.forEach((lng) => {
        setWindowObject(lng, key, fallbackValue, true);
    });
};

export const resolveManifestLoadPath = (
    manifest: TranslationManifestResponse
) => {
    const apiBase = getApiPath("locales").replace(/\/locales$/, "");
    const path = manifest.load_path.replace("/api/{version}", apiBase);
    const separator = path.includes("?") ? "&" : "?";
    return `${path}${separator}catalog=${encodeURIComponent(
        manifest.catalog_version
    )}`;
};

const loadManifest = async (): Promise<TranslationManifestResponse> => {
    try {
        const response = await fetch(getApiPath("locales"), {
            credentials: "same-origin",
            headers: { Accept: "application/json" },
        });
        if (!response.ok) {
            throw new Error(
                `Translation manifest request failed: ${response.status}`
            );
        }
        const manifest = (await response.json()) as TranslationManifestResponse;
        if (
            !manifest.catalog_version ||
            !manifest.load_path ||
            !Array.isArray(manifest.locales) ||
            manifest.locales.length === 0 ||
            manifest.locales.some(
                (locale) => !locale.namespaces.includes(MERGED_NAMESPACE)
            )
        ) {
            throw new Error("Translation manifest is incomplete.");
        }
        return manifest;
    } catch (error) {
        throw error instanceof Error
            ? error
            : new Error("Translation manifest could not be loaded.");
    }
};

/**
 * Translation catalogs are backend-owned. React configures i18next from the
 * backend manifest and never embeds a second copy of locale JSON.
 */
export const createI18nOptions = (
    manifest: TranslationManifestResponse
): InitOptions => {
    const supportedLngs = manifest.locales.map((locale) => locale.language_tag);
    const loadPath = resolveManifestLoadPath(manifest);

    return {
        debug: I18N_DEBUG_MODE(),
        defaultNS: MERGED_NAMESPACE,
        ns: [MERGED_NAMESPACE],
        // The backend merged resource applies each advertised namespace's
        // fallback policy. Disable client cross-locale fallback when that
        // manifest is available so controlled SDS/GHS text is never
        // replaced with en-US text by i18next.
        fallbackLng: false,
        supportedLngs,
        load: "currentOnly",
        backend: { loadPath },
        interpolation: {
            escapeValue: false,
        },
        missingKeyHandler,
        saveMissing: true,
    };
};

const runtimeConfigFromManifest = (
    manifest: TranslationManifestResponse
): TranslationRuntimeConfig => {
    const applicationLocales = manifest.locales.map((locale) => ({
        value: locale.language_tag,
        label: locale.native_name,
    }));
    const defaultLocale =
        applicationLocales.find((locale) => locale.value === "en-US")?.value ??
        applicationLocales[0].value;

    return { manifest, applicationLocales, defaultLocale };
};

const initializeI18n = async (): Promise<TranslationRuntimeConfig> => {
    const manifest = await loadManifest();

    if (I18N_STORE_KEYS_TO_VARIABLE() && manifest) {
        setOnWindow("i18manifest", manifest);
    }

    const singleton = i18n as ViridisI18n;
    if (!singleton.__viridisPluginsConfigured) {
        i18n.use(HttpBackend).use(LanguageDetector);
        singleton.__viridisPluginsConfigured = true;
    }
    await i18n.init(createI18nOptions(manifest));
    const runtimeConfig = runtimeConfigFromManifest(manifest);
    singleton.__viridisRuntimeConfig = runtimeConfig;
    return runtimeConfig;
};

type ViridisI18n = typeof i18n & {
    __viridisInitialization?: Promise<TranslationRuntimeConfig>;
    __viridisPluginsConfigured?: boolean;
    __viridisRuntimeConfig?: TranslationRuntimeConfig;
};

const initializedTestRuntime = (): TranslationRuntimeConfig => {
    const configuredLanguages = i18n.options?.supportedLngs;
    const supportedLngs = (
        Array.isArray(configuredLanguages) ? configuredLanguages : []
    ).filter(
        (language): language is string =>
            typeof language === "string" && language !== "cimode"
    );
    const testLanguages = supportedLngs.length > 0 ? supportedLngs : ["en-US"];
    const applicationLocales = testLanguages.map((language) => ({
        value: language,
        label: language,
    }));
    const manifest: TranslationManifestResponse = {
        catalog_version: "jest",
        load_path: "",
        locales: applicationLocales.map((locale) => ({
            locale: locale.value as never,
            language_tag: locale.value,
            native_name: locale.label,
            namespaces: [MERGED_NAMESPACE, TranslationNamespace.SDS],
            fallback_policies: {},
        })),
    };
    return {
        manifest,
        applicationLocales,
        defaultLocale: applicationLocales[0]?.value ?? "en-US",
    };
};

const initializeI18nOnce = (): Promise<TranslationRuntimeConfig> => {
    const singleton = i18n as ViridisI18n;
    if (singleton.__viridisRuntimeConfig) {
        return Promise.resolve(singleton.__viridisRuntimeConfig);
    }
    if (singleton.isInitialized && process.env.NODE_ENV === "test") {
        singleton.__viridisRuntimeConfig = initializedTestRuntime();
        return Promise.resolve(singleton.__viridisRuntimeConfig);
    }
    if (!singleton.__viridisInitialization) {
        singleton.__viridisInitialization = initializeI18n().catch((error) => {
            delete singleton.__viridisInitialization;
            throw error;
        });
    }
    return singleton.__viridisInitialization;
};

export const i18nReady = initializeI18nOnce();
void i18nReady;

export const retryI18nInitialization = async () => {
    const singleton = i18n as ViridisI18n;
    delete singleton.__viridisInitialization;
    delete singleton.__viridisRuntimeConfig;
    return initializeI18nOnce();
};

export const hasTranslationResource = (
    language: string,
    namespace: TranslationNamespace
) => {
    if (!i18n.isInitialized || !i18n.hasResourceBundle(language, namespace)) {
        return false;
    }
    const bundle = i18n.getResourceBundle(language, namespace);
    return Boolean(
        bundle && typeof bundle === "object" && Object.keys(bundle).length > 0
    );
};

const t = ((key: string, ...args: unknown[]) => {
    if (!i18n.isInitialized) {
        const options = args[0];
        if (options && typeof options === "object") {
            const defaultValue = (options as { defaultValue?: unknown })
                .defaultValue;
            if (typeof defaultValue === "string") {
                return defaultValue;
            }
        }
        return key;
    }
    return (i18n.t as (...translationArgs: unknown[]) => string)(key, ...args);
}) as typeof i18n.t;

export { i18n, t };

export default t;
